import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const AcademicSuccess = () => {
  const { currentUser } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handlePurchase = () => {
    if (!agreed) {
      alert("Please agree to the terms and conditions");
      return;
    }

    setProcessing(true);
    // Replace with your Yoco payment link
    window.open("https://pay.yoco.com/gradpath-sa-pty-ltd", "_blank");
    setProcessing(false);
  };

  const features = [
    {
      icon: "fas fa-chalkboard-user",
      title: "Academic Mentorship",
      description:
        "One-on-one mentorship from industry professionals and academics. Get guidance on your coursework, research projects, and academic career path. Regular check-ins and personalized advice to help you excel.",
      color: "from-blue-500 to-blue-700",
    },
    {
      icon: "fas fa-briefcase",
      title: "Internship Placement Assistance",
      description:
        "Dedicated support to find and secure internships that align with your field of study. Resume reviews, application tracking, and direct connections to our partner companies.",
      color: "from-green-500 to-green-700",
    },
    {
      icon: "fas fa-laptop-code",
      title: "Tech + Professional Integration",
      description:
        "Learn industry-relevant tech skills, professional soft skills, and workplace etiquette. Workshops on digital tools, communication, teamwork, and professional networking.",
      color: "from-purple-500 to-purple-700",
    },
    {
      icon: "fas fa-users",
      title: "Peer Learning Community",
      description:
        "Access to a community of like-minded students. Group study sessions, project collaboration, and networking events with fellow programme participants.",
      color: "from-orange-500 to-orange-700",
    },
    {
      icon: "fas fa-certificate",
      title: "Skill Development Workshops",
      description:
        "Regular workshops on critical skills including public speaking, technical writing, data analysis, and project management. Earn certificates upon completion.",
      color: "from-red-500 to-red-700",
    },
    {
      icon: "fas fa-calendar-check",
      title: "12-Month Access",
      description:
        "Full access to all programme resources, mentorship sessions, and workshops for one full year. Track your progress and achievements throughout your academic journey.",
      color: "from-teal-500 to-teal-700",
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
          <i className="fas fa-graduation-cap text-white text-3xl"></i>
        </div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gradpath-dark mb-4">
          Academic Success Programme
        </h1>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Empower your academic journey with mentorship, internship support, and
          professional development
        </p>

        {/* Price Tag */}
        <div className="mt-6 inline-flex items-center gap-4 bg-gradient-to-r from-gradpath-green to-gradpath-dark text-white px-8 py-4 rounded-full shadow-lg">
          <span className="text-2xl font-bold">R600</span>
          <span className="text-white/80">one-time payment</span>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
            12 months access
          </span>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {features.map((feature, index) => (
          <div
            key={index}
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all group overflow-hidden"
          >
            <div className={`bg-gradient-to-r ${feature.color} p-4 text-white`}>
              <i className={`${feature.icon} text-3xl`}></i>
            </div>
            <div className="p-5">
              <h3 className="text-xl font-bold text-gradpath-dark mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-12">
        <h2 className="text-2xl font-bold text-gradpath-dark mb-6 text-center">
          How It Works
        </h2>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradpath-green rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-2xl font-bold">1</span>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Enroll & Pay</h3>
            <p className="text-gray-500 text-sm">
              Complete payment and create your account
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradpath-green rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-2xl font-bold">2</span>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Get Matched</h3>
            <p className="text-gray-500 text-sm">
              We match you with a mentor in your field
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradpath-green rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-2xl font-bold">3</span>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Start Learning</h3>
            <p className="text-gray-500 text-sm">
              Access workshops and community resources
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradpath-green rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-2xl font-bold">4</span>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Get Placed</h3>
            <p className="text-gray-500 text-sm">
              Receive internship placement assistance
            </p>
          </div>
        </div>
      </div>

      {/* Who Is This For */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-12">
        <h2 className="text-2xl font-bold text-gradpath-dark mb-6 text-center">
          Who Is This Programme For?
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <i className="fas fa-user-graduate text-3xl text-gradpath-green mb-2"></i>
            <p className="font-semibold">University Students</p>
            <p className="text-sm text-gray-500">
              Undergraduate & postgraduate
            </p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <i className="fas fa-school text-3xl text-gradpath-green mb-2"></i>
            <p className="font-semibold">TVET College Students</p>
            <p className="text-sm text-gray-500">
              All diploma and certificate programmes
            </p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <i className="fas fa-chart-line text-3xl text-gradpath-green mb-2"></i>
            <p className="font-semibold">Recent Matriculants</p>
            <p className="text-sm text-gray-500">
              Planning your tertiary education
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-xl p-8 text-center text-white mb-12">
        <h2 className="text-2xl md:text-3xl font-bold mb-3">
          Invest in Your Academic Future
        </h2>
        <p className="text-white/90 mb-6">
          Join students who have transformed their academic journey with our
          support
        </p>

        {!currentUser && (
          <div className="mb-4 p-3 bg-white/20 rounded-lg inline-block">
            <i className="fas fa-info-circle mr-2"></i>
            Please login or create an account before purchasing
          </div>
        )}

        <button
          onClick={handlePurchase}
          disabled={!currentUser || processing}
          className="bg-white text-gradpath-green hover:bg-gray-100 font-bold py-3 px-8 rounded-full shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? (
            <i className="fas fa-spinner fa-spin mr-2"></i>
          ) : (
            <i className="fas fa-shopping-cart mr-2"></i>
          )}
          Enroll Now - R600
        </button>

        <p className="text-white/70 text-sm mt-4">
          Secure payment via Yoco • Instant access after payment • Valid for 12
          months
        </p>
      </div>

      {/* Disclaimers */}
      <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-yellow-500">
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <i className="fas fa-exclamation-triangle text-yellow-500"></i>
          Important Information
        </h3>

        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex gap-2">
            <i className="fas fa-ban text-red-500 mt-0.5"></i>
            <p>
              <strong className="text-red-600">No Internship Guarantee:</strong>{" "}
              The Academic Success Programme provides mentorship, skill
              development, and internship placement assistance. However, we do
              not guarantee internship placement. Success depends on your
              academic performance, application quality, and availability of
              positions.
            </p>
          </div>

          <div className="flex gap-2">
            <i className="fas fa-credit-card text-red-500 mt-0.5"></i>
            <p>
              <strong className="text-red-600">No Refunds:</strong> All payments
              for the Academic Success Programme are non-refundable. Once
              enrolled, you receive immediate access to mentorship and
              resources.
            </p>
          </div>

          <div className="flex gap-2">
            <i className="fas fa-clock text-gray-500 mt-0.5"></i>
            <p>
              <strong>Programme Duration:</strong> Access to services is valid
              for 12 months from the date of enrollment.
            </p>
          </div>

          <div className="flex gap-2">
            <i className="fas fa-shield-alt text-gray-500 mt-0.5"></i>
            <p>
              <strong>Terms Apply:</strong> By enrolling, you agree to our{" "}
              <Link to="/terms" className="text-gradpath-green hover:underline">
                Terms & Conditions
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                className="text-gradpath-green hover:underline"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>

        {/* Terms Agreement Checkbox */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.value)}
              className="mt-0.5 w-4 h-4 text-gradpath-green rounded focus:ring-gradpath-green"
            />
            <span className="text-sm text-gray-700">
              I understand that the Academic Success Programme offers
              mentorship, skill development, and internship assistance, but does
              not guarantee internship placement. I also acknowledge that
              payments are non-refundable.
            </span>
          </label>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-xl shadow-md p-6 mt-8">
        <h2 className="text-2xl font-bold text-gradpath-dark mb-6 text-center">
          Programme Comparison
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="px-4 py-3 text-left"></th>
                <th className="px-4 py-3 text-center bg-green-50 rounded-t-lg">
                  Academic Success
                </th>
                <th className="px-4 py-3 text-center bg-blue-50 rounded-t-lg">
                  Career Success
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="px-4 py-3 font-semibold">Academic Mentorship</td>
                <td className="px-4 py-3 text-center text-green-600">
                  <i className="fas fa-check-circle"></i>
                </td>
                <td className="px-4 py-3 text-center text-gray-400">
                  <i className="fas fa-times-circle"></i>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="px-4 py-3 font-semibold">
                  Internship Placement Assistance
                </td>
                <td className="px-4 py-3 text-center text-green-600">
                  <i className="fas fa-check-circle"></i>
                </td>
                <td className="px-4 py-3 text-center text-gray-400">
                  <i className="fas fa-times-circle"></i>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="px-4 py-3 font-semibold">
                  Tech + Professional Integration
                </td>
                <td className="px-4 py-3 text-center text-green-600">
                  <i className="fas fa-check-circle"></i>
                </td>
                <td className="px-4 py-3 text-center text-green-600">
                  <i className="fas fa-check-circle"></i>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="px-4 py-3 font-semibold">CV Optimization</td>
                <td className="px-4 py-3 text-center text-gray-400">
                  <i className="fas fa-times-circle"></i>
                </td>
                <td className="px-4 py-3 text-center text-green-600">
                  <i className="fas fa-check-circle"></i>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="px-4 py-3 font-semibold">
                  Interview Preparation
                </td>
                <td className="px-4 py-3 text-center text-gray-400">
                  <i className="fas fa-times-circle"></i>
                </td>
                <td className="px-4 py-3 text-center text-green-600">
                  <i className="fas fa-check-circle"></i>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="px-4 py-3 font-semibold">Duration</td>
                <td className="px-4 py-3 text-center font-semibold">
                  12 months
                </td>
                <td className="px-4 py-3 text-center font-semibold">
                  3 months
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-semibold">Price</td>
                <td className="px-4 py-3 text-center font-bold text-gradpath-green">
                  R600
                </td>
                <td className="px-4 py-3 text-center font-bold text-gradpath-green">
                  R600
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AcademicSuccess;
